import { useEffect, useState, type ReactNode } from 'react';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { FiBell, FiX } from 'react-icons/fi';
import { HiOutlineUserCircle } from 'react-icons/hi2';
import MyProfileForm from './MyProfileForm';
import NotificationPreferences from './NotificationPreferences';
import { NotificationPreferenceScope } from '@/apis/notificationPreferences/types';

type Section = 'account' | 'notifications';

type Props = {
  open: boolean;
  section: Section;
  scope: NotificationPreferenceScope;
  showNotifications?: boolean;
  onClose: () => void;
};

export default function ProfileSettingsDialog({ open, section, scope, showNotifications = true, onClose }: Props) {
  const [active, setActive] = useState<Section>(section);
  const sectionToShow = showNotifications ? section : 'account';

  useEffect(() => {
    if (open) setActive(sectionToShow);
  }, [open, sectionToShow]);

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1300,
        '& .MuiDrawer-paper': {
          width: { xs: '100%', md: '80%' },
          maxWidth: 1100,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid #E6E6E6',
          flexShrink: 0
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 600, lineHeight: '24px', color: '#1C1C1C' }}>
          Profile and Settings
        </Typography>
        <IconButton onClick={onClose} aria-label='Close profile and settings' sx={{ color: '#6B6B6B' }}>
          <FiX size={20} />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', minHeight: 0, flex: 1 }}>
        <Box sx={{ width: 240, flexShrink: 0, px: 1.5, py: 2, bgcolor: '#fff' }}>
          <NavButton
            selected={active === 'account'}
            icon={<HiOutlineUserCircle size={18} />}
            label='My Profile'
            onClick={() => setActive('account')}
          />
          {showNotifications && (
            <NavButton
              selected={active === 'notifications'}
              icon={<FiBell size={16} strokeWidth={1.75} />}
              label='Notification Preferences'
              onClick={() => setActive('notifications')}
            />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', bgcolor: '#F8F9FB', p: 2 }}>
          <Box
            sx={{
              bgcolor: '#fff',
              border: '1px solid #E8E8E8',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)'
            }}
          >
            {active === 'account' ? <MyProfileForm /> : <NotificationPreferences scope={scope} />}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}

function NavButton({
  selected,
  icon,
  label,
  onClick
}: {
  selected: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <Box
      component='button'
      type='button'
      onClick={onClick}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        mb: 0.5,
        px: 1.25,
        py: 1,
        border: 0,
        borderRadius: '6px',
        cursor: 'pointer',
        textAlign: 'left',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'inherit',
        backgroundColor: selected ? '#D1EAFC' : 'transparent',
        color: '#1C1C1C',
        fontWeight: selected ? 500 : 400,
        fontSize: 13,
        lineHeight: '20px',
        '&:hover': { backgroundColor: selected ? '#D1EAFC' : '#F5F5F5' },
        '& svg': { flexShrink: 0, color: '#4B4B4B' }
      }}
    >
      {icon}
      {label}
    </Box>
  );
}
